import React, { useState, useEffect } from 'react';
import useQuranReader from '../hooks/useQuranReader';
import { quranStructure } from '../utils/constants';

const JumpToDialog = ({ isOpen, onClose }) => {
  const { navigateTo, currentSurah, currentVerse } = useQuranReader();
  const [selectedSurah, setSelectedSurah] = useState(currentSurah);
  const [selectedVerse, setSelectedVerse] = useState(currentVerse === 0 ? 1 : currentVerse);
  const [maxVerse, setMaxVerse] = useState(1);

  useEffect(() => {
    const surah = quranStructure.find(s => s.number === parseInt(selectedSurah));
    if (surah) {
      setMaxVerse(surah.totalVerses);
      if (selectedVerse > surah.totalVerses) {
        setSelectedVerse(1);
      }
    }
  }, [selectedSurah, selectedVerse]);

  useEffect(() => {
    if (isOpen) {
      setSelectedSurah(currentSurah);
      setSelectedVerse(currentVerse === 0 ? 1 : currentVerse);
    }
  }, [isOpen, currentSurah, currentVerse]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const surahNum = parseInt(selectedSurah);
    const verseNum = parseInt(selectedVerse);
    
    if (surahNum >= 1 && surahNum <= 114 && verseNum >= 1 && verseNum <= maxVerse) {
      navigateTo(surahNum, verseNum);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label="Jump to Verse">
      <div 
        className="rounded-lg shadow-xl max-w-md w-full p-6 animate-fadeIn"
        style={{ backgroundColor: 'var(--color-card-bg)' }}
      >
        <h2 className="text-xl font-bold text-heading mb-4 font-kufi">Jump to Verse</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Surah selection */}
            <div>
              <label 
                htmlFor="surah-select" 
                className="block text-sm font-medium mb-1 font-lato"
                style={{ color: 'var(--color-charcoal)' }}
              >
                Surah
              </label>
              <select 
                id="surah-select"
                className="w-full p-2 border rounded-md shadow-sm font-lato"
                style={{ 
                  borderColor: 'var(--color-input-border)', 
                  backgroundColor: 'var(--color-input-bg)',
                  color: 'var(--color-charcoal)' 
                }}
                value={selectedSurah}
                onChange={(e) => setSelectedSurah(parseInt(e.target.value))}
              >
                {quranStructure.map(surah => (
                  <option key={surah.number} value={surah.number}>
                    {surah.number}: {surah.englishName} ({surah.name})
                  </option>
                ))}
              </select>
            </div>
            
            {/* Verse selection */}
            <div>
              <label 
                htmlFor="verse-select" 
                className="block text-sm font-medium mb-1 font-lato"
                style={{ color: 'var(--color-charcoal)' }}
              >
                Verse (1-{maxVerse})
              </label>
              <input 
                id="verse-select"
                type="number" 
                min="1" 
                max={maxVerse}
                className="w-full p-2 border rounded-md shadow-sm font-lato"
                style={{ 
                  borderColor: 'var(--color-input-border)', 
                  backgroundColor: 'var(--color-input-bg)',
                  color: 'var(--color-charcoal)' 
                }}
                value={selectedVerse}
                onChange={(e) => setSelectedVerse(Math.min(Math.max(1, parseInt(e.target.value) || 1), maxVerse))}
              />
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button 
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md transition-colors font-lato"
              style={{ backgroundColor: 'var(--color-light-gray)', color: 'var(--color-charcoal)' }}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="px-4 py-2 bg-deep-green rounded-md transition-colors font-lato"
              style={{ color: 'var(--color-header-text)' }}
            >
              Go to Verse
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JumpToDialog;